import React, { useState, useEffect } from 'react';
import api from '../../../services/api';

function PartnerActionConfigsTab({ partner, onViewActions }) {
  const [actionConfigs, setActionConfigs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedConfig, setSelectedConfig] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    actionType: '',
    description: '',
    clientId: '',
    isActive: true
  });
  const [message, setMessage] = useState(null);

  useEffect(() => {
    if (partner) {
      fetchActionConfigs();
    }
  }, [partner]);

  const fetchActionConfigs = async () => {
    try {
      setLoading(true);
      const response = await api.getPartnerActionConfigs(partner._id);
      setActionConfigs(response.data.actionConfigs || []);
    } catch (err) {
      console.error('Error fetching action configs:', err);
      setMessage({ type: 'error', text: 'Failed to load action configurations' });
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setSelectedConfig(null);
    setFormData({
      actionType: '',
      description: '',
      clientId: '',
      isActive: true
    });
    setShowForm(true);
    setMessage(null);
  };

  const handleEdit = (config) => {
    setSelectedConfig(config);
    setFormData({
      actionType: config.actionType || '',
      description: config.description || '',
      clientId: config.clientId || '',
      isActive: config.isActive !== undefined ? config.isActive : true
    });
    setShowForm(true);
    setMessage(null);
  };

  const handleDelete = async (configId) => {
    if (!window.confirm('Are you sure you want to delete this action configuration?')) {
      return;
    }

    try {
      await api.deletePartnerActionConfig(partner._id, configId);
      setMessage({ type: 'success', text: 'Action configuration deleted successfully' });
      fetchActionConfigs();
    } catch (err) {
      console.error('Error deleting action config:', err);
      setMessage({ type: 'error', text: 'Failed to delete action configuration' });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Trim all string fields to prevent leading/trailing spaces
    const trimmedData = {
      ...formData,
      actionType: formData.actionType.trim(),
      description: formData.description.trim(),
      clientId: formData.clientId.trim()
    };

    try {
      if (selectedConfig) {
        // Update existing config
        await api.updatePartnerActionConfig(partner._id, selectedConfig._id, trimmedData);
        setMessage({ type: 'success', text: 'Action configuration updated successfully' });
      } else {
        // Create new config
        await api.createPartnerActionConfig(partner._id, trimmedData);
        setMessage({ type: 'success', text: 'Action configuration created successfully' });
      }

      setShowForm(false);
      fetchActionConfigs();
    } catch (err) {
      console.error('Error saving action config:', err);
      setMessage({ type: 'error', text: 'Failed to save action configuration' });
    }
  };

  const handleCancel = () => {
    setShowForm(false);
    setSelectedConfig(null);
  };

  const inputStyle = {
    width: '100%',
    padding: '8px 12px',
    border: '1px solid #e5e7eb',
    borderRadius: '6px',
    fontSize: '14px'
  };

  const labelStyle = {
    display: 'block',
    fontSize: '14px',
    fontWeight: '500',
    color: '#374151',
    marginBottom: '6px'
  };

  return (
    <div>
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '20px'
      }}>
        <h2 style={{ margin: 0, fontSize: '20px' }}>
          Action Configurations
        </h2>
        {!showForm && (
          <button
            onClick={handleCreate}
            style={{
              padding: '8px 16px',
              backgroundColor: '#4f46e5',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              fontSize: '14px',
              fontWeight: '500',
              cursor: 'pointer'
            }}
          >
            + Create Action Config
          </button>
        )}
      </div>

      {message && (
        <div style={{
          padding: '12px',
          marginBottom: '20px',
          borderRadius: '6px',
          backgroundColor: message.type === 'success' ? '#dcfce7' : '#fee2e2',
          color: message.type === 'success' ? '#166534' : '#dc2626',
          border: `1px solid ${message.type === 'success' ? '#86efac' : '#fca5a5'}`
        }}>
          {message.text}
        </div>
      )}

      {showForm ? (
        <div style={{
          padding: '20px',
          backgroundColor: '#f9fafb',
          border: '1px solid #e5e7eb',
          borderRadius: '8px'
        }}>
          <h3 style={{ marginTop: 0, marginBottom: '20px', fontSize: '18px' }}>
            {selectedConfig ? 'Edit Action Configuration' : 'Create Action Configuration'}
          </h3>

          <form onSubmit={handleSubmit}>
            <div style={{ marginBottom: '15px' }}>
              <label style={labelStyle}>Action Type</label>
              <input
                type="text"
                value={formData.actionType}
                onChange={(e) => setFormData({ ...formData, actionType: e.target.value })}
                style={inputStyle}
                placeholder="e.g., login, purchase, signup"
                required
              />
            </div>

            <div style={{ marginBottom: '15px' }}>
              <label style={labelStyle}>Description</label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                style={{ ...inputStyle, minHeight: '80px', resize: 'vertical' }}
                placeholder="Describe what this action configuration is for"
              />
            </div>

            <div style={{ marginBottom: '15px' }}>
              <label style={labelStyle}>Client ID (optional)</label>
              <input
                type="text"
                value={formData.clientId}
                onChange={(e) => setFormData({ ...formData, clientId: e.target.value })}
                style={inputStyle}
                placeholder="Specific client application ID"
              />
            </div>

            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={formData.isActive}
                  onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                  style={{ marginRight: '8px' }}
                />
                <span style={{ fontSize: '14px', fontWeight: '500', color: '#374151' }}>
                  Active
                </span>
              </label>
            </div>

            <div style={{ display: 'flex', gap: '10px' }}>
              <button
                type="submit"
                style={{
                  padding: '8px 16px',
                  backgroundColor: '#4f46e5',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: 'pointer'
                }}
              >
                {selectedConfig ? 'Update' : 'Create'}
              </button>
              <button
                type="button"
                onClick={handleCancel}
                style={{
                  padding: '8px 16px',
                  backgroundColor: '#ffffff',
                  color: '#374151',
                  border: '1px solid #e5e7eb',
                  borderRadius: '6px',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      ) : loading ? (
        <div style={{ textAlign: 'center', padding: '40px', color: '#6b7280' }}>
          Loading action configurations...
        </div>
      ) : actionConfigs.length === 0 ? (
        <div style={{
          textAlign: 'center',
          padding: '40px',
          backgroundColor: '#f9fafb',
          border: '1px solid #e5e7eb',
          borderRadius: '8px',
          color: '#6b7280'
        }}>
          <p>No action configurations found</p>
          <p style={{ fontSize: '14px', marginTop: '10px' }}>
            Create your first action configuration to get started
          </p>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
          {actionConfigs.map((config) => (
            <div
              key={config._id}
              style={{
                padding: '20px',
                backgroundColor: '#ffffff',
                border: '1px solid #e5e7eb',
                borderRadius: '8px'
              }}
            >
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'start',
                marginBottom: '10px'
              }}>
                <div style={{ flex: 1 }}>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px',
                    marginBottom: '8px'
                  }}>
                    <h3 style={{
                      margin: 0,
                      fontSize: '16px',
                      fontFamily: 'monospace',
                      color: '#111827'
                    }}>
                      {config.actionType}
                    </h3>
                    <span style={{
                      display: 'inline-block',
                      padding: '2px 8px',
                      borderRadius: '100px',
                      fontSize: '11px',
                      fontWeight: '500',
                      backgroundColor: config.isActive ? '#dcfce7' : '#fee2e2',
                      color: config.isActive ? '#166534' : '#dc2626'
                    }}>
                      {config.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                  {config.description && (
                    <p style={{ margin: '0 0 8px 0', fontSize: '14px', color: '#6b7280' }}>
                      {config.description}
                    </p>
                  )}
                  {config.clientId && (
                    <p style={{ margin: 0, fontSize: '12px', color: '#9ca3af' }}>
                      Client ID: {config.clientId}
                    </p>
                  )}
                </div>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <button
                    onClick={() => onViewActions && onViewActions(config._id, config.actionType)}
                    style={{
                      padding: '6px 12px',
                      backgroundColor: '#f3f4f6',
                      color: '#374151',
                      border: '1px solid #e5e7eb',
                      borderRadius: '6px',
                      fontSize: '13px',
                      cursor: 'pointer'
                    }}
                  >
                    View Actions →
                  </button>
                  <button
                    onClick={() => handleEdit(config)}
                    style={{
                      padding: '6px 12px',
                      backgroundColor: '#f3f4f6',
                      color: '#374151',
                      border: '1px solid #e5e7eb',
                      borderRadius: '6px',
                      fontSize: '13px',
                      cursor: 'pointer'
                    }}
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => handleDelete(config._id)}
                    style={{
                      padding: '6px 12px',
                      backgroundColor: '#fee2e2',
                      color: '#dc2626',
                      border: '1px solid #fca5a5',
                      borderRadius: '6px',
                      fontSize: '13px',
                      cursor: 'pointer'
                    }}
                  >
                    Delete
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default PartnerActionConfigsTab;
